import { Flex, ResponsiveValue, Text } from '@chakra-ui/react';
import {
  ButtonIcon,
  FileUpload,
  FormFeedback,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { deleteUploadedFile } from 'src/api/queries';
import { FieldLabel } from '../Field';
import { AxiosProgressEvent } from 'axios';
import { FormFieldFileUploadArray as IFormFieldFileUploadArray } from '@publishing-engine/domain/types/article';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import axiosInstance from 'src/api/client';
import { getFileExtension, parseUploadError } from 'src/utils/file';
import { get } from 'lodash';
import {
  getSignedPrivateAccessUrl,
  getSignedPrivateUploadUrl
} from '../../api/queries';
import { IFileUploaded } from './common/types';
import { useState } from 'react';
import { AllowedFiles } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/UploadSupplement';
import { useDuplicateConfirm } from 'src/hooks/useDuplicateConfirm';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';

interface IFormFieldMultipleFileUploadProps {
  config: Omit<IFormFieldFileUploadArray, 'component'>;
  submissionContextId?: string;
  submissionId?: string;
  // temp fix for email attachments
  _usePublicUploads?: true;
  isEditingAllowed?: boolean;
  helperText?: string;
  width?: ResponsiveValue<string>;
  submissionFields?: ISubmissionFieldsResponse | unknown;
}

const MAX_FILES_AT_ONCE = 15;

const FormFieldMultipleFileUpload: React.FC<
  IFormFieldMultipleFileUploadProps
> = ({
  config,
  submissionId,
  submissionFields,
  _usePublicUploads,
  isEditingAllowed = true,
  helperText,
  width = '100%'
}) => {
  const toast = useToast();
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    register,
    getValues
  } = useFormContext<{ [key: string]: IFileUploaded[] }>();
  const {
    caseDataFields: { main: field },
    params: { labels, validations },
    name
  } = config;
  const { checkIsDuplicateFile, duplicateFileModal } = useDuplicateConfirm();
  const error = errors[field];
  const mainError = get(errors, field);
  const [uploadRejectedFileError, setUploadRejectedFileError] = useState<
    string | null
  >(null);
  const { append, remove, update, fields } = useFieldArray({
    control,
    name: field
  });

  const getUploadUrl = (extension: string) => {
    if (!submissionId) {
      throw new Error(
        'FormFieldMultipleFileUpload: no submission id. ' + field
      );
    }

    // if (_usePublicUploads)
    //   return getSignedUnprotectedUploadUrl(submissionId, extension);

    return getSignedPrivateUploadUrl(submissionId, extension);
  };

  const handleOpenFile = async (url: string) => {
    if (!submissionId) {
      return;
    }
    const signedUrl = await getSignedPrivateAccessUrl(submissionId, url);
    window.open(signedUrl, '_blank');
  };

  const uploadFile = async (file: File, index: number) => {
    setUploadRejectedFileError('');
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: IFileUploaded = {
      name: file.name,
      referenceName: file.name,
      progress: 0,
      isUploading: true,
      extension: fileExtension,
      url: ''
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };
      update(index, fileToUpload);
    };

    try {
      append(fileToUpload);

      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);
      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        referenceName: original_filename,
        extension: fileExtension
      };
      clearErrors(`${field}.${index}`);
      update(index, fileToUpload);
    } catch (e) {
      const message = parseUploadError(e);

      setError(`${field}.${index}`, {
        type: 'upload-error',
        message
      });
    } finally {
      update(index, {
        ...fileToUpload,
        isUploading: false
      });
    }
  };

  const removeFile = async (index: number, fileId?: string) => {
    remove(index);
    fileId && deleteUploadedFile(fileId);
  };
  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    clearErrors(field);
    setUploadRejectedFileError('');
    let uploadIndex = fields?.length || 0;
    const currentFields =
      typeof submissionFields === 'object' && submissionFields !== null
        ? {
            ...submissionFields,
            [name]: getValues()?.fileSet ?? getValues()
          }
        : getValues()?.fileSet ?? getValues();
    for (const file of acceptedFiles) {
      const canUpload = await checkIsDuplicateFile(
        { fieldName: field, name: file?.name },
        currentFields
      );
      if (canUpload) {
        uploadFile(file, uploadIndex++);
      }
    }
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] =
    async rejectedFiles => {
      if (!rejectedFiles || rejectedFiles.length === 0) return;

      // Separate rejected files into 'too many files' and other errors
      const tooManyFiles = rejectedFiles.filter(r =>
        r.errors.some(e => e.code === 'too-many-files')
      );
      const otherRejected = rejectedFiles.filter(
        r => !r.errors.some(e => e.code === 'too-many-files')
      );

      // Upload only allowed number of files from 'too many files'
      if (tooManyFiles.length > 0 && MAX_FILES_AT_ONCE > 0) {
        const filesToUpload = tooManyFiles
          .slice(0, MAX_FILES_AT_ONCE)
          .map(r => r.file);

        await Promise.all(
          filesToUpload.map((file, index) =>
            uploadFile(file, index + fields.length)
          )
        );

        toast({
          variant: 'attention',
          title: 'File Upload Limit Exceeded',
          description:
            'You can only upload up to ' +
            MAX_FILES_AT_ONCE +
            ' files at once. Excess files were not uploaded.',
          isClosable: true
        });
      }

      // Handle other rejected files (wrong type, too large, etc.)
      if (otherRejected.length > 0) {
        const messages = otherRejected.map(({ file, errors }) => {
          const reasons = errors.map(err => {
            switch (err.code) {
              case 'file-invalid-type':
                return 'Unsupported file type';
              case ErrorCode.FileTooLarge:
                return `File is larger than ${validations.maxSizeMB}MB`;
              default:
                return 'Upload rejected';
            }
          });
          return `${file.name}: ${reasons.join(', ')}`;
        });

        setUploadRejectedFileError(messages.join(','));
      }
    };

  const disableDeleteButton = () => {
    if (fields.length > 0) {
      return fields.some(file => file.isUploading);
    }
    return false;
  };

  const isDeleteButtonDisabled = disableDeleteButton();

  return (
    <>
      <Flex flexDirection="column" width="100%">
        {isEditingAllowed && (
          <Flex flexDirection="column" gap={2} marginBottom={2}>
            <Flex flexDirection="column">
              <FieldLabel
                isRequired={validations.required}
                label={labels.main}
                subText={labels.subtitle}
                helpText={labels.helpText}
              />
            </Flex>
            <Flex flexDirection="column" w={width}>
              <FileUpload
                {...register(field)}
                uploadButtonText="Select file(s)"
                multiple
                onDrop={onDrop}
                onDropRejected={onDropRejected}
                maxFiles={MAX_FILES_AT_ONCE}
                maxSize={validations.maxSizeMB * 1000000}
                isInvalid={Boolean(error?.message)}
                accept={validations.allowedFileFormats}
              />
            </Flex>
            <Text
              textStyle="textContext"
              alignSelf={helperText ? 'start' : ''}
            ></Text>
            <Text textStyle="textContext" alignSelf={helperText ? 'start' : ''}>
              {helperText && helperText !== '' ? (
                `Accepted format(s): ${helperText}. Max size: ${validations.maxSizeMB}MB.`
              ) : (
                <>
                  <AllowedFiles allowedFile={validations.allowedFileFormats} />.
                  Max size: {validations.maxSizeMB}MB. Executable scripts are
                  not permitted.
                </>
              )}
            </Text>
          </Flex>
        )}
        {!isEditingAllowed && (
          <FieldLabel
            isRequired={validations.required}
            label="Attached Files"
            subText=""
            helpText={labels.helpText}
          />
        )}
        {fields.length ? (
          <Flex flexDirection="column" gap={1}>
            {fields.map((file, index) => (
              <Flex
                key={file.id || file.url}
                flexDirection="column"
                _last={{ borderBottom: 'none' }}
                gap={2}
              >
                <Flex
                  flexDirection="row"
                  borderColor="neutralPrimaryBorder"
                  width="100%"
                >
                  {file.isUploading ? (
                    <Text textStyle="textContext">Uploading</Text>
                  ) : (
                    <>
                      <Flex alignItems="center" gap={2} flexDirection="row">
                        {helperText ? (
                          <a
                            onClick={() => handleOpenFile(file.url)}
                            style={{
                              cursor: 'pointer',
                              width: '100%',
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              textAlign: 'initial'
                            }}
                          >
                            <FormFeedback variant="success">
                              {file.name}
                            </FormFeedback>
                          </a>
                        ) : (
                          <>
                            <a
                              onClick={() => handleOpenFile(file.url)}
                              style={{
                                cursor: 'pointer',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal'
                              }}
                            >
                              <FormFeedback variant="success">
                                {file.name}{' '}
                              </FormFeedback>
                            </a>
                          </>
                        )}
                      </Flex>
                      {isEditingAllowed && (
                        <ButtonIcon
                          size="small"
                          ariaLabel={`${file.name} delete`}
                          onClick={() => removeFile(index, file.fileId)}
                          disabled={isDeleteButtonDisabled}
                          icon={<FontAwesomeIcon icon={faTrashAlt} />}
                          variant="secondary"
                        />
                      )}
                    </>
                  )}
                </Flex>
                {!file.isUploading && mainError?.[index] && (
                  <FormFeedback variant="error">
                    {mainError?.[index]?.message}
                  </FormFeedback>
                )}
              </Flex>
            ))}
          </Flex>
        ) : null}
        {uploadRejectedFileError ? (
          <Flex flexDirection="column" gap={2}>
            {(uploadRejectedFileError?.split(',') as string[]).map((msg, i) => (
              <FormFeedback key={i} variant="error">
                {msg}
              </FormFeedback>
            ))}
          </Flex>
        ) : (
          mainError?.type === 'manual' && (
            <FormFeedback variant="error">{mainError?.message}</FormFeedback>
          )
        )}
      </Flex>
      {duplicateFileModal}
    </>
  );
};

export default FormFieldMultipleFileUpload;
